import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import EMark from "@/components/EMark";

/**
 * EmailSignature — premium HTML signature for Eren Bilen, rendered exactly
 * as it will appear inside Gmail / Outlook / Apple Mail.
 *
 * Implementation note:
 * Email signatures must use HTML <table> + inline styles. Modern CSS
 * (flex, grid, custom-properties, web fonts) is not reliable across mail
 * clients. We hard-code premium system serif/sans stacks that already
 * exist on every device, plus the warm Erenown gold (#E6A55A) as a literal
 * hex value, and the engraved-grey palette established on the website.
 */

const PALETTE = {
  warm:    "#E6A55A",
  text:    "#0F1115",
  muted:   "#5A6068",
  hairline:"#D4D2CC",
  accent:  "#1F4F66",
};

const SERIF =
  "'Times New Roman', Georgia, 'Iowan Old Style', serif";
const SANS =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
const MONO =
  "'SF Mono', 'Menlo', 'Consolas', 'Courier New', monospace";

const DETAILS = {
  name: "Eren Bilen",
  role: "Founder & Director",
  email: "eren.bilen@erenown.com",
  website: "www.erenown.com",
  phones: [
    { label: "TR", value: "+90 507 251 9061", tel: "+905072519061" },
    { label: "UK", value: "+44 7999 006313",  tel: "+447999006313" },
    { label: "XK", value: "+383 49 737340",   tel: "+38349737340"  },
  ],
};

const DISCLAIMER =
  "This email and any attachments are confidential and intended solely for the named recipient(s). If you have received this message in error, please notify the sender immediately and delete it. Any unauthorized use, disclosure, copying, or distribution is prohibited. The views expressed in this email are those of the sender and do not necessarily reflect the views of Erenown Ltd. Erenown Ltd does not guarantee the accuracy, completeness, or security of this email or its attachments and accepts no liability for any loss or damage resulting from viruses, transmission errors, or unauthorized modifications. Unless expressly stated otherwise, this email does not create any legally binding obligation, commitment, quotation, or contract on behalf of Erenown Ltd.";

/**
 * SignatureA — full corporate signature (current).
 * Two-column wordmark + details, entities row, full disclaimer.
 * For first contact, formal correspondence, business introductions.
 */
function SignatureA() {
  // For the on-screen preview, the outer wrapper provides padding and
  // the email-app background colour. For the clipboard copy, we omit
  // that wrapper so the signature pastes cleanly into the compose window.
  const inner = (
    <table
      cellPadding="0"
      cellSpacing="0"
      border="0"
      role="presentation"
      style={{
        borderCollapse: "collapse",
        fontFamily: SANS,
        color: PALETTE.text,
        lineHeight: 1.5,
        fontSize: "13px",
        maxWidth: "560px",
      }}
    >
      <tbody>
        <tr>
          {/* Wordmark column */}
          <td
            valign="top"
            style={{
              padding: "0 22px 0 0",
              borderRight: `1px solid ${PALETTE.hairline}`,
              whiteSpace: "nowrap",
            }}
          >
            <div
              style={{
                fontFamily: SANS,
                fontWeight: 800,
                fontSize: "24px",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: PALETTE.text,
                lineHeight: 1,
              }}
            >
              <span style={{ color: PALETTE.warm }}>E</span>
              <span>&nbsp;renown</span>
            </div>
            <div
              style={{
                marginTop: "10px",
                fontFamily: MONO,
                fontSize: "9.5px",
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                color: PALETTE.muted,
              }}
            >
              Central Structure
            </div>
          </td>

          {/* Details column */}
          <td valign="top" style={{ padding: "0 0 0 22px", verticalAlign: "top" }}>
            <div
              style={{
                fontFamily: SERIF,
                fontSize: "20px",
                lineHeight: 1.15,
                color: PALETTE.text,
                fontWeight: 400,
                letterSpacing: "-0.005em",
              }}
            >
              {DETAILS.name}
            </div>
            <div
              style={{
                marginTop: "4px",
                fontFamily: MONO,
                fontSize: "9.5px",
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                color: PALETTE.warm,
              }}
            >
              {DETAILS.role}
            </div>

            <table
              cellPadding="0"
              cellSpacing="0"
              border="0"
              role="presentation"
              style={{
                marginTop: "14px",
                borderCollapse: "collapse",
                fontFamily: SANS,
                fontSize: "12.5px",
                lineHeight: 1.65,
              }}
            >
              <tbody>
                <tr>
                  <td style={{ paddingRight: "10px", color: PALETTE.muted, fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.24em", textTransform: "uppercase", verticalAlign: "top" }}>
                    Email
                  </td>
                  <td style={{ verticalAlign: "top" }}>
                    <a
                      href={`mailto:${DETAILS.email}`}
                      style={{ color: PALETTE.text, textDecoration: "none", borderBottom: `1px solid ${PALETTE.warm}` }}
                    >
                      {DETAILS.email}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingRight: "10px", color: PALETTE.muted, fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.24em", textTransform: "uppercase", verticalAlign: "top" }}>
                    Web
                  </td>
                  <td style={{ verticalAlign: "top" }}>
                    <a
                      href="https://www.erenown.com"
                      style={{ color: PALETTE.text, textDecoration: "none" }}
                    >
                      {DETAILS.website}
                    </a>
                  </td>
                </tr>
                {DETAILS.phones.map((p) => (
                  <tr key={p.label}>
                    <td style={{ paddingRight: "10px", color: PALETTE.muted, fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.24em", textTransform: "uppercase", verticalAlign: "top" }}>
                      {p.label}
                    </td>
                    <td style={{ verticalAlign: "top" }}>
                      <a href={`tel:${p.tel}`} style={{ color: PALETTE.text, textDecoration: "none" }}>
                        {p.value}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>

        {/* Hairline + entities row */}
        <tr>
          <td colSpan="2" style={{ paddingTop: "20px" }}>
            <div style={{ borderTop: `1px solid ${PALETTE.hairline}`, paddingTop: "14px" }}>
              <table
                cellPadding="0"
                cellSpacing="0"
                border="0"
                role="presentation"
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontFamily: SANS,
                  fontSize: "11px",
                  lineHeight: 1.6,
                  color: PALETTE.muted,
                }}
              >
                <tbody>
                  <tr>
                    <td style={{ paddingRight: "20px", verticalAlign: "top" }}>
                      <span style={{ fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.28em", textTransform: "uppercase", color: PALETTE.text }}>
                        UK
                      </span>{" "}
                      &nbsp; Erenown Ltd · Registered in England and Wales · Co. No. 12845592 · 83 St Marks Road, Enfield, EN1 1BJ, United Kingdom
                    </td>
                  </tr>
                  <tr>
                    <td style={{ paddingTop: "5px", verticalAlign: "top" }}>
                      <span style={{ fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.28em", textTransform: "uppercase", color: PALETTE.text }}>
                        XK
                      </span>{" "}
                      &nbsp; Erenown LLC · Ulpianë, C2, H-III, Nr.6, Bulevardi “Dëshmorët e Kombit”, Prishtinë 10000, Republic of Kosovo
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </td>
        </tr>

        {/* Disclaimer */}
        <tr>
          <td colSpan="2" style={{ paddingTop: "16px" }}>
            <div
              style={{
                fontFamily: SANS,
                fontSize: "10px",
                lineHeight: 1.6,
                color: "#8A8F97",
                maxWidth: "560px",
              }}
            >
              {DISCLAIMER}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );

  return inner;
}

/**
 * SignatureB — "Editorial Letterhead".
 * Stacked, centred composition. Large monumental warm "E" punctuates the
 * top, name in serif beneath, role in warm mono caps, contact items laid
 * out as a single elegant inline row of mono labels separated by warm
 * dots. Two thin rules frame the entities line; no disclaimer. For
 * day-to-day correspondence and replies — premium but lighter.
 */
function SignatureB() {
  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      border="0"
      role="presentation"
      style={{
        borderCollapse: "collapse",
        fontFamily: SANS,
        color: PALETTE.text,
        lineHeight: 1.5,
        fontSize: "13px",
        maxWidth: "620px",
        width: "100%",
      }}
    >
      <tbody>
        {/* Wordmark + monumental anchor */}
        <tr>
          <td style={{ paddingBottom: "16px" }}>
            <div
              style={{
                fontFamily: SANS,
                fontWeight: 800,
                fontSize: "22px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: PALETTE.text,
                lineHeight: 1,
              }}
            >
              <span style={{ color: PALETTE.warm }}>E</span>
              <span>&nbsp;renown</span>
            </div>
          </td>
        </tr>

        {/* Hairline */}
        <tr>
          <td style={{ borderTop: `1px solid ${PALETTE.hairline}`, height: "1px", lineHeight: 0, fontSize: 0 }}>&nbsp;</td>
        </tr>

        {/* Name + role */}
        <tr>
          <td style={{ paddingTop: "16px" }}>
            <div
              style={{
                fontFamily: SERIF,
                fontSize: "22px",
                lineHeight: 1.15,
                color: PALETTE.text,
                fontWeight: 400,
                letterSpacing: "-0.005em",
              }}
            >
              {DETAILS.name}
            </div>
            <div
              style={{
                marginTop: "5px",
                fontFamily: MONO,
                fontSize: "10px",
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                color: PALETTE.warm,
              }}
            >
              {DETAILS.role} · Erenown
            </div>
          </td>
        </tr>

        {/* Inline contact row — pipe-separated for legibility */}
        <tr>
          <td style={{ paddingTop: "14px" }}>
            <div style={{ fontFamily: SANS, fontSize: "12.5px", color: PALETTE.text, lineHeight: 1.7 }}>
              <a
                href={`mailto:${DETAILS.email}`}
                style={{ color: PALETTE.text, textDecoration: "none", borderBottom: `1px solid ${PALETTE.warm}` }}
              >
                {DETAILS.email}
              </a>
              <span style={{ color: PALETTE.warm, padding: "0 8px" }}>·</span>
              <a href="https://www.erenown.com" style={{ color: PALETTE.text, textDecoration: "none" }}>
                {DETAILS.website}
              </a>
            </div>
            <div style={{ marginTop: "6px", fontFamily: SANS, fontSize: "12.5px", color: PALETTE.text, lineHeight: 1.7 }}>
              {DETAILS.phones.map((p, i) => (
                <span key={p.label}>
                  {i > 0 && <span style={{ color: PALETTE.warm, padding: "0 8px" }}>·</span>}
                  <span style={{ fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.24em", textTransform: "uppercase", color: PALETTE.muted, marginRight: "5px" }}>
                    {p.label}
                  </span>
                  <a href={`tel:${p.tel}`} style={{ color: PALETTE.text, textDecoration: "none" }}>
                    {p.value}
                  </a>
                </span>
              ))}
            </div>
          </td>
        </tr>

        {/* Entities — single line, light */}
        <tr>
          <td style={{ paddingTop: "18px" }}>
            <div style={{ borderTop: `1px solid ${PALETTE.hairline}`, paddingTop: "10px" }}>
              <div
                style={{
                  fontFamily: SANS,
                  fontSize: "10.5px",
                  lineHeight: 1.7,
                  color: PALETTE.muted,
                }}
              >
                <span style={{ fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.28em", textTransform: "uppercase", color: PALETTE.text }}>UK</span>{" "}
                Erenown Ltd · Co. No. 12845592 · Enfield, EN1 1BJ
                <span style={{ color: PALETTE.warm, padding: "0 8px" }}>·</span>
                <span style={{ fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.28em", textTransform: "uppercase", color: PALETTE.text }}>XK</span>{" "}
                Erenown LLC · Prishtinë 10000
              </div>
              <div
                style={{
                  marginTop: "4px",
                  fontFamily: SANS,
                  fontSize: "10.5px",
                  lineHeight: 1.6,
                  color: PALETTE.muted,
                  fontStyle: "italic",
                }}
              >
                Operating through independently established Erenown entities in the United Kingdom and Kosovo.
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * SignatureC — "Compact Crest".
 * The most reduced version: a small crest of monumental warm "E" + serif
 * name on one line, role beneath, then a minimal three-row contact stack.
 * No website caption, no entities, no disclaimer. Designed for ongoing
 * threads and quick replies where the full corporate footprint would
 * feel heavy. Pairs naturally with SignatureA on first contact.
 */
function SignatureC() {
  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      border="0"
      role="presentation"
      style={{
        borderCollapse: "collapse",
        fontFamily: SANS,
        color: PALETTE.text,
        lineHeight: 1.5,
        fontSize: "13px",
        maxWidth: "440px",
      }}
    >
      <tbody>
        <tr>
          {/* Monumental E */}
          <td
            valign="middle"
            style={{
              padding: "0 18px 0 0",
              borderRight: `1px solid ${PALETTE.hairline}`,
              whiteSpace: "nowrap",
            }}
          >
            <div
              style={{
                fontFamily: SANS,
                fontWeight: 800,
                fontSize: "44px",
                letterSpacing: "-0.04em",
                textTransform: "uppercase",
                color: PALETTE.warm,
                lineHeight: 1,
              }}
            >
              E
            </div>
          </td>

          {/* Name + role + email */}
          <td valign="middle" style={{ padding: "0 0 0 18px", verticalAlign: "middle" }}>
            <div
              style={{
                fontFamily: SERIF,
                fontSize: "19px",
                lineHeight: 1.1,
                color: PALETTE.text,
                fontWeight: 400,
                letterSpacing: "-0.005em",
              }}
            >
              {DETAILS.name}
            </div>
            <div
              style={{
                marginTop: "3px",
                fontFamily: MONO,
                fontSize: "9.5px",
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: PALETTE.warm,
              }}
            >
              {DETAILS.role} · Erenown
            </div>
            <div
              style={{
                marginTop: "8px",
                fontFamily: SANS,
                fontSize: "12.5px",
                color: PALETTE.text,
              }}
            >
              <a
                href={`mailto:${DETAILS.email}`}
                style={{ color: PALETTE.text, textDecoration: "none", borderBottom: `1px solid ${PALETTE.warm}` }}
              >
                {DETAILS.email}
              </a>
              <span style={{ color: PALETTE.warm, padding: "0 8px" }}>·</span>
              <a href="https://www.erenown.com" style={{ color: PALETTE.text, textDecoration: "none" }}>
                {DETAILS.website}
              </a>
            </div>
            <div
              style={{
                marginTop: "4px",
                fontFamily: SANS,
                fontSize: "12px",
                color: PALETTE.muted,
                lineHeight: 1.65,
              }}
            >
              {DETAILS.phones.map((p, i) => (
                <span key={p.label}>
                  {i > 0 && <span style={{ color: PALETTE.warm, padding: "0 6px" }}>·</span>}
                  <a href={`tel:${p.tel}`} style={{ color: PALETTE.muted, textDecoration: "none" }}>
                    {p.value}
                  </a>
                </span>
              ))}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default function EmailSignaturePage() {
  const sigRef = useRef(null);
  const [copied, setCopied] = useState(null);
  const [variant, setVariant] = useState("A");

  const VARIANTS = [
    {
      key: "A",
      label: "Corporate Full",
      tagline: "First contact · Formal correspondence",
      description:
        "The full corporate signature. Wordmark column, name and details on the right, both Erenown entities laid out beneath, and the complete confidentiality disclaimer. Use for first contacts, business introductions, and any thread that benefits from full legal disclosure.",
      Component: SignatureA,
    },
    {
      key: "B",
      label: "Editorial Letterhead",
      tagline: "Day-to-day correspondence · Replies",
      description:
        "A lighter, stacked composition. Wordmark at the top, hairline divider, name in serif, role in warm caps, and a single elegant inline row of contact details separated by warm dots. Both entities reduced to one line. No disclaimer. Premium, calm, and noticeably less heavy.",
      Component: SignatureB,
    },
    {
      key: "C",
      label: "Compact Crest",
      tagline: "Ongoing threads · Quick replies",
      description:
        "The most reduced version. A small crest of the monumental warm E next to your serif name, role beneath, and three concise contact lines. No website caption, no entities, no disclaimer. Pairs naturally with the Corporate Full version on first contact, then carries the conversation forward without weight.",
      Component: SignatureC,
    },
  ];

  const Active = VARIANTS.find((v) => v.key === variant)?.Component || SignatureA;

  const copyHTML = async () => {
    if (!sigRef.current) return;
    const html = sigRef.current.innerHTML;
    const plain = sigRef.current.innerText;
    let succeeded = false;

    // 1) Modern Async Clipboard API path (Gmail, Outlook for Web, etc.)
    try {
      if (typeof window !== "undefined" && window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
        succeeded = true;
      }
    } catch (_) { /* fall through to legacy path */ }

    // 2) Legacy execCommand path — select the rendered DOM and copy.
    //    This works inside iframes where the modern API is blocked.
    if (!succeeded) {
      try {
        const range = document.createRange();
        range.selectNodeContents(sigRef.current);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        succeeded = document.execCommand("copy");
        sel.removeAllRanges();
      } catch (_) { /* still failing — give the user the manual option */ }
    }

    if (succeeded) {
      setCopied("html");
      setTimeout(() => setCopied(null), 2400);
    } else {
      setCopied("fail");
      setTimeout(() => setCopied(null), 4500);
    }
  };

  const copyRawHTML = async () => {
    if (!sigRef.current) return;
    let succeeded = false;
    const raw = sigRef.current.outerHTML;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(raw);
        succeeded = true;
      }
    } catch (_) { /* fall through */ }
    if (!succeeded) {
      // Legacy fallback via temporary textarea
      try {
        const ta = document.createElement("textarea");
        ta.value = raw;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        succeeded = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) { /* nothing more to try */ }
    }
    if (succeeded) {
      setCopied("raw");
      setTimeout(() => setCopied(null), 2400);
    } else {
      setCopied("fail");
      setTimeout(() => setCopied(null), 4500);
    }
  };

  /**
   * openCleanWindow — opens the signature on a standalone page in a new
   * tab, with no app chrome and no iframe restrictions. The user can then
   * Cmd/Ctrl+A then Cmd/Ctrl+C. This bypasses any preview-iframe block on
   * the Clipboard API and is the most reliable fallback.
   */
  const openCleanWindow = () => {
    if (!sigRef.current) return;
    const html = sigRef.current.innerHTML;
    const win = window.open("", "_blank");
    if (!win) {
      setCopied("popup-blocked");
      setTimeout(() => setCopied(null), 4000);
      return;
    }
    win.document.open();
    win.document.write(`<!doctype html><html><head><meta charset="utf-8" />
<title>Erenown signature ${variant} — select all, copy</title>
<style>
  body{margin:0;background:#F9F7F2;color:#0F1115;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;
    padding:48px;}
  .helpbar{max-width:640px;margin:0 auto 32px;padding:14px 18px;
    border:1px solid #D4D2CC;background:#fff;
    font-size:12px;letter-spacing:0.18em;text-transform:uppercase;color:#5A6068;}
  .helpbar b{color:#E6A55A;letter-spacing:0.18em;}
  .stage{max-width:640px;margin:0 auto;padding:32px 36px;
    border:1px solid #D4D2CC;background:#fff;}
</style></head><body>
<div class="helpbar"><b>Step 1</b> Click anywhere in the signature below &nbsp; <b>Step 2</b> Press Cmd/Ctrl + A &nbsp; <b>Step 3</b> Press Cmd/Ctrl + C</div>
<div class="stage" contenteditable="false">${html}</div>
</body></html>`);
    win.document.close();
  };

  return (
    <div data-testid="email-signature-page" style={{ background: T.bg, color: T.text, minHeight: "100vh" }}>
      {/* Header strip */}
      <section className="relative overflow-hidden" style={{ background: T.bg }}>
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at center, ${T.border}66 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent 82%)",
          }}
        />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-12">
          <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.muted }}>
            <span className="inline-flex items-center gap-2"><EMark /> · MAIL · SIGNATURE</span>
            <span className="flex-1 h-px" style={{ background: T.border }} />
            <span style={{ color: T.accent }}>For Eren Bilen</span>
          </div>
          <h1 className="font-fraunces font-light text-4xl md:text-5xl lg:text-[56px] leading-[1.04] tracking-[-0.01em] mt-8 max-w-3xl" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
            Email signature, ready to <span className="italic" style={{ color: T.accent }}>paste</span>.
          </h1>
          <p className="mt-6 max-w-xl font-intertight text-[15px] leading-[1.85]" style={{ color: T.textSoft }}>
            Designed in the same visual register as the website. Copy it once, paste it into your mail client's signature settings, and every outgoing message carries the same calm, premium identity.
          </p>
        </div>
      </section>

      {/* Variant tabs */}
      <section className="border-t" style={{ borderColor: T.border, background: T.bg }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-10">
          <div className="font-plexmono text-[10px] tracking-[0.32em] uppercase mb-5" style={{ color: T.muted }}>
            Choose a version
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="signature-variants">
            {VARIANTS.map((v) => {
              const isActive = v.key === variant;
              return (
                <button
                  key={v.key}
                  type="button"
                  data-testid={`variant-${v.key.toLowerCase()}`}
                  onClick={() => setVariant(v.key)}
                  className="text-left p-5 transition-colors"
                  style={{
                    border: `1px solid ${isActive ? T.warm : T.border}`,
                    background: isActive ? `${T.warm}10` : "transparent",
                    color: T.text,
                  }}
                >
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <span className="font-plexmono text-[10.5px] tracking-[0.32em] uppercase" style={{ color: isActive ? T.warm : T.muted }}>
                      {v.key} · {v.label}
                    </span>
                    {isActive && (
                      <span className="font-plexmono text-[9px] tracking-[0.3em] uppercase" style={{ color: T.warm }}>
                        Active
                      </span>
                    )}
                  </div>
                  <div
                    className="font-fraunces text-[15px] leading-[1.4] mb-2"
                    style={{ color: T.text, fontVariationSettings: "'SOFT' 40" }}
                  >
                    {v.tagline}
                  </div>
                  <p className="font-intertight text-[12.5px] leading-[1.7]" style={{ color: T.textSoft }}>
                    {v.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Live preview on a paper-like card */}
      <section className="border-t" style={{ borderColor: T.border, background: T.surface }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="font-plexmono text-[10px] tracking-[0.32em] uppercase mb-4" style={{ color: T.muted }}>
            Preview · {variant} · as the recipient sees it
          </div>

          {/* Mock email canvas — soft warm-white paper, like a real inbox */}
          <div
            style={{
              background: "#F9F7F2",
              border: `1px solid ${T.border}`,
              padding: "32px 36px",
              borderRadius: "0px",
            }}
            data-testid="signature-preview"
          >
            {/* Mock email body line for context */}
            <div
              style={{
                fontFamily: SANS,
                fontSize: "13px",
                color: PALETTE.text,
                lineHeight: 1.65,
                marginBottom: "26px",
              }}
            >
              Best regards,
            </div>

            {/* Hidden node used by the copy button — mirrors what gets copied */}
            <div ref={sigRef} key={variant}>
              <Active />
            </div>
          </div>

          {/* Action row */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="copy-signature"
              onClick={copyHTML}
              className="inline-flex items-center gap-3 px-6 py-3.5 font-plexmono text-[11px] tracking-[0.3em] uppercase"
              style={{ background: T.warm, color: T.bg, border: `1px solid ${T.warm}` }}
            >
              {copied === "html" ? "Copied — paste into your mail client" : "Copy signature"}
            </button>
            <button
              type="button"
              data-testid="copy-raw-html"
              onClick={copyRawHTML}
              className="inline-flex items-center gap-3 px-6 py-3.5 font-plexmono text-[11px] tracking-[0.3em] uppercase border transition-colors"
              style={{ borderColor: T.border, color: T.text, background: "transparent" }}
            >
              {copied === "raw" ? "HTML copied" : "Copy raw HTML"}
            </button>
            <button
              type="button"
              data-testid="open-in-new-tab"
              onClick={openCleanWindow}
              className="inline-flex items-center gap-3 px-6 py-3.5 font-plexmono text-[11px] tracking-[0.3em] uppercase border transition-colors"
              style={{ borderColor: T.accent, color: T.accent, background: "transparent" }}
            >
              Open in new tab to copy
            </button>
            {copied === "fail" && (
              <span className="font-plexmono text-[10px] tracking-[0.24em] uppercase" style={{ color: "#D67272" }}>
                Clipboard blocked here — use “Open in new tab to copy”
              </span>
            )}
            {copied === "popup-blocked" && (
              <span className="font-plexmono text-[10px] tracking-[0.24em] uppercase" style={{ color: "#D67272" }}>
                Pop-up blocked — please allow pop-ups for this site and try again
              </span>
            )}
          </div>

          <p className="mt-5 max-w-2xl font-intertight text-[13px] leading-[1.85]" style={{ color: T.textSoft }}>
            <strong style={{ color: T.text }}>Tip — preview environments:</strong> if “Copy signature” doesn’t work here, that’s usually because the site is running inside a preview iframe and the browser blocks clipboard writes from iframes. Use <strong>Open in new tab to copy</strong>: a clean window opens with just the signature, ready for Cmd/Ctrl + A then Cmd/Ctrl + C.
          </p>
        </div>
      </section>

      {/* How to install */}
      <section className="border-t" style={{ borderColor: T.border, background: T.bg }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="font-plexmono text-[10px] tracking-[0.32em] uppercase mb-6" style={{ color: T.accent }}>
            How to install
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Steps
              title="Gmail"
              steps={[
                'Click "Copy signature" above.',
                "Open Gmail → Settings (gear) → See all settings → General.",
                'Scroll to "Signature", click "Create new", give it a name.',
                "Paste into the signature editor (Cmd/Ctrl + V).",
                "Set it as default for new mails and replies, then Save changes.",
              ]}
            />
            <Steps
              title="Outlook (web & desktop)"
              steps={[
                'Click "Copy signature" above.',
                "Open Outlook → Settings → Mail → Compose and reply.",
                'Under "Email signature", click "+ New signature".',
                "Paste into the editor (Cmd/Ctrl + V).",
                "Choose default signatures for new messages and replies, then Save.",
              ]}
            />
            <Steps
              title="Apple Mail"
              steps={[
                'Click "Copy signature" above.',
                "Open Mail → Settings → Signatures.",
                'Choose your account, click "+" to create a new signature.',
                'Uncheck "Always match my default message font".',
                "Paste into the right-hand box and close — it saves automatically.",
              ]}
            />
          </div>

          <p className="mt-12 max-w-2xl font-intertight text-[13.5px] leading-[1.85]" style={{ color: T.textSoft }}>
            The signature uses universal system fonts and inline HTML so it
            renders consistently across Gmail, Outlook, Apple Mail, iOS Mail,
            Android Gmail, and most third-party clients. No images are
            embedded, so it never gets blocked or stripped.
          </p>
        </div>
      </section>

      {/* Footer back link */}
      <section className="border-t" style={{ borderColor: T.border }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-10 flex flex-wrap items-center justify-between gap-4">
          <p className="max-w-md font-intertight text-[13.5px] leading-[1.85]" style={{ color: T.textSoft }}>
            If you'd like a slightly more compact version (without the disclaimer paragraph), or one that includes a small JPG/PNG monogram for clients that allow images, tell me and I'll prepare it.
          </p>
          <Link
            to="/"
            className="font-plexmono text-[11px] tracking-[0.3em] uppercase border-b pb-1"
            style={{ color: T.accent, borderColor: T.accent }}
          >
            Back to site
          </Link>
        </div>
      </section>
    </div>
  );
}

function Steps({ title, steps }) {
  return (
    <div>
      <h3 className="font-fraunces text-2xl mb-4" style={{ color: T.text }}>
        {title}
      </h3>
      <ol className="space-y-3 font-intertight text-[14px] leading-[1.7]" style={{ color: T.textSoft }}>
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span
              className="font-plexmono tabular-nums text-[10px] tracking-[0.2em] mt-1.5"
              style={{ color: T.warm, minWidth: "1.5rem" }}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
